import { useEffect, useMemo, useState } from 'react';
import {
  QCC_BALANCE_CHAINS,
  QccNetworkId,
} from '../common/constants/qcc-balance-chains';
import {
  formatQcc,
  getQccBalance,
} from '../common/services/qcc-contract/qcc-contract.service';

export type QccBalanceRow = {
  id: QccNetworkId;
  name: string;
  icon: string;
  amount: number;
  amountWei: bigint;
};

export type QccBalancesStatus = 'idle' | 'loading' | 'ready' | 'error';

export function useQccBalances(walletAddress: string | null) {
  const [rows, setRows] = useState<QccBalanceRow[]>(() =>
    QCC_BALANCE_CHAINS.map(chain => ({
      id: chain.id,
      name: chain.name,
      icon: chain.icon,
      amount: 0,
      amountWei: BigInt(0),
    })),
  );
  const [status, setStatus] = useState<QccBalancesStatus>('idle');

  useEffect(() => {
    if (!walletAddress) {
      setRows(
        QCC_BALANCE_CHAINS.map(chain => ({
          id: chain.id,
          name: chain.name,
          icon: chain.icon,
          amount: 0,
          amountWei: BigInt(0),
        })),
      );
      setStatus('idle');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');

    const loadBalances = async () => {
      try {
        const results = await Promise.all(
          QCC_BALANCE_CHAINS.map(async chain => {
            const balanceWei = await getQccBalance(chain.chainKey, walletAddress);
            const balanceQcc = parseFloat(formatQcc(balanceWei, 2));
            return {
              id: chain.id,
              name: chain.name,
              icon: chain.icon,
              amount: Number.isFinite(balanceQcc) ? balanceQcc : 0,
              amountWei: BigInt(balanceWei.toString()),
            };
          }),
        );

        if (!cancelled) {
          setRows(results);
          setStatus('ready');
        }
      } catch {
        if (!cancelled) {
          setStatus('error');
        }
      }
    };

    loadBalances();

    return () => {
      cancelled = true;
    };
  }, [walletAddress]);

  const total = useMemo(
    () => rows.reduce((sum, row) => sum + row.amount, 0),
    [rows],
  );

  return { rows, total, status };
}
